import { useCallback, useEffect, useRef, useState } from "react";

export const useDisableClickEvent = (
	ref: React.MutableRefObject<HTMLElement | null>,
	options?: { allowedClasses?: string[]; includeButtonChildren?: boolean; refreshCounter?: number; clickThroughParentToFind?: string[] },
) => {

	const allowedClasses = useCallback(() => options?.allowedClasses || [], [options?.allowedClasses]);
	const clickThroughParentToFind = useCallback(() => options?.clickThroughParentToFind || [], [options?.clickThroughParentToFind]);
	const includeButtonChildren = useCallback(() => options?.includeButtonChildren || false, [options?.includeButtonChildren]);
	const refreshCounter = options?.refreshCounter || 0;

	// What's with the counter tracker? Well, we can't put too much logic
	// in the click handler before preventing the default behavior, or it won't work.
	// So we create a simple counter to track the number of clicks on the registration wrapper
	// and only allow the click event to pass through if the counter is 1.
	const lastClickedClass = useRef(0);

	useEffect(() => {
		// disable all clicks on the registration wrapper and all children
		const wrapper = ref.current;
		if (!wrapper) return;

		const handleClick = (e: MouseEvent) => {
			// we must stop the event propagation before doing any logic or it won't work
			// because the event will have already propagated to the parent
			if (lastClickedClass.current !== 1) {
				e.stopPropagation();
				e.stopImmediatePropagation();
				e.preventDefault();
			}

			const target = e.target as HTMLElement;
			const matched = allowedClasses()?.some(className => target.classList.contains(className));

			// if we're allowed to click, then we'll dispatch a click event on the target
			if (matched) {
				lastClickedClass.current = lastClickedClass.current + 1;
				// this valdidation prevents double clicking on the same element, or even worse,
				// infinite loops of clicks on the same element causing a maximum call stack error
				if (lastClickedClass.current === 1) {
					// set detail to 1 to allow our play/pause button to work in controls.tsx
					target.dispatchEvent(new MouseEvent('click', { bubbles: true, detail: 1 }));
				} else {
					lastClickedClass.current = 0;
				}
			} else {
				// if target has a parent that is a button and it matches, we need to trigger the click event on the button
				if (includeButtonChildren()) {
					const parentButton = target.closest('button') || target.closest('[role="button"]');
					if (parentButton) {
						const parentButtonMatched = allowedClasses()?.some(className => parentButton.classList.contains(className));
						if (parentButtonMatched) {
							lastClickedClass.current = lastClickedClass.current + 1;
							parentButton.dispatchEvent(new MouseEvent('click', { bubbles: true, detail: 1 }));
							return;
						}
					}

					const clickThroughParent = target.closest('.click-through-parent');
					if (clickThroughParent) {
						const childElementMatched = clickThroughParentToFind()?.map(className => clickThroughParent.querySelector(`.${className}`)).filter(Boolean);
						if (childElementMatched.length) {
							childElementMatched?.[0]?.dispatchEvent(new MouseEvent('click', { bubbles: true, detail: 1 }));
						}
					}

					const matchedParent = target.closest(`.${allowedClasses()?.join(', .')}`);
					if (matchedParent) {
						lastClickedClass.current = lastClickedClass.current + 1;
						matchedParent.dispatchEvent(new MouseEvent('click', { bubbles: true, detail: 1 }));
					}
				}
			}

		};

		wrapper.addEventListener('click', handleClick);
		return () => {
			wrapper.removeEventListener('click', handleClick);
		};
	}, [ref, allowedClasses, includeButtonChildren, refreshCounter, clickThroughParentToFind]);
};
